import React from 'react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import * as Colors from '../theme/colors';
import { SessionSecurityControl } from '../services/contractors';

type Props = {
  open: boolean;
  policy: SessionSecurityControl | null;
  targetName: string;
  onAccept: () => void;
  onDecline: () => void;
};

function clipboardSentence(policy: SessionSecurityControl): string | null {
  if (policy.clipboardAccess === 'off') {
    return 'Copy and paste between your machine and this session are disabled.';
  }
  if (policy.clipboardAccess === 'pasteOnly') {
    return 'You can paste into this session, but you cannot copy anything out of it.';
  }
  return null;
}

const RecordingNotice: React.FC<Props> = ({ open, policy, targetName, onAccept, onDecline }) => {
  if (!policy) return null;

  const clipboard = clipboardSentence(policy);

  return (
    <Dialog
      open={open}
      onClose={onDecline}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: Colors.Surface,
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            p: 1,
          },
        },
      }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiberManualRecordIcon sx={{ fontSize: 14, color: Colors.Red }} />
          <Typography sx={{ color: Colors.White, fontSize: 16, fontWeight: 600 }}>
            This session is recorded
          </Typography>
        </Box>

        <Typography sx={{ color: Colors.LightGray, fontSize: 13, lineHeight: 1.6 }}>
          Everything you do on {targetName} is recorded and kept for audit. The recording is
          available to the administrators of the organization that granted you access.
        </Typography>

        {policy.watermarking.enabled && (
          <Typography sx={{ color: Colors.LightGray, fontSize: 13, lineHeight: 1.6 }}>
            Your identity and the session time are shown on screen for the duration of the session.
          </Typography>
        )}

        {clipboard && (
          <Typography sx={{ color: Colors.LightGray, fontSize: 13, lineHeight: 1.6 }}>
            {clipboard}
          </Typography>
        )}

        {!policy.enableFileTransfer && (
          <Typography sx={{ color: Colors.LightGray, fontSize: 13, lineHeight: 1.6 }}>
            File transfer is disabled for this session.
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
          <Button
            variant="contained"
            onClick={onAccept}
            sx={{ flex: 1, textTransform: 'none', fontSize: 13 }}
          >
            I understand, connect
          </Button>
          <Button
            variant="outlined"
            onClick={onDecline}
            sx={{ flex: 1, textTransform: 'none', fontSize: 13, color: Colors.LightGray }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RecordingNotice;
